import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Heart, Link2, Copy, Check, X, Settings, Users, Sparkles, Moon, Smile, Leaf, Shield, AlertTriangle, Send, MessageHeart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset love messages
const LOVE_MESSAGES = [
  { emoji: "💗", text: "Thinking of you and sending healing energy your way 🌿" },
  { emoji: "🌟", text: "You are my favorite person to heal with 💫" },
  { emoji: "🙏🏾", text: "Grateful for you and our journey together" },
  { emoji: "🌸", text: "Your peace is my priority. I love you 💜" },
  { emoji: "✨", text: "We're growing stronger every day, together" },
  { emoji: "🌙", text: "May your day be filled with calm and clarity" },
  { emoji: "🦋", text: "Watching you bloom is my greatest joy" },
  { emoji: "🌻", text: "You make my soul smile" }
];

const HealingTogether = ({ isOpen, onClose }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bondStatus, setBondStatus] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSendLove, setShowSendLove] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [sendingLove, setSendingLove] = useState(false);

  useEffect(() => {
    if (isOpen && token) {
      fetchBondStatus();
    }
  }, [isOpen, token]);

  const fetchBondStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/healing-together/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBondStatus(response.data);
      if (response.data.my_privacy_settings) {
        setPrivacySettings(response.data.my_privacy_settings);
      }
    } catch (error) {
      console.error("Error fetching bond status:", error);
      toast.error("Failed to load Healing Together status");
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    try {
      const response = await axios.post(`${API}/healing-together/invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setInviteCode(response.data.invite_code);
        setShowInvite(true);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error("Failed to create invite");
    }
  };

  const acceptInvite = async () => {
    if (!joinCode || joinCode.length < 8) {
      toast.error("Please enter a valid 8-character code");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/healing-together/accept?invite_code=${joinCode.toUpperCase()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowJoin(false);
        setJoinCode("");
        fetchBondStatus();
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept invite");
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const savePrivacySettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/healing-together/privacy`, privacySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Privacy settings updated! 🔒");
      setShowPrivacy(false);
      fetchBondStatus();
    } catch (error) {
      toast.error("Failed to save privacy settings");
    } finally {
      setSaving(false);
    }
  };

  const disconnectBond = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Soul Bond? This cannot be undone.")) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/healing-together/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success("Soul Bond disconnected");
        fetchBondStatus();
      }
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const togglePrivacy = (itemId) => {
    setPrivacySettings(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const sendLoveMessage = async (message) => {
    setSendingLove(true);
    try {
      await axios.post(
        `${API}/healing-together/send-love`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Love sent to ${bondStatus?.partner_name}! 💗`);
      setShowSendLove(false);
      setCustomMessage("");
    } catch (error) {
      toast.error("Failed to send love message");
    } finally {
      setSendingLove(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            Healing Together
          </DialogTitle>
          <DialogDescription>
            Soul Bond Mode - Share your wellness journey with your partner
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Heart className="w-8 h-8 mx-auto mb-2 text-pink-500 animate-pulse" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : !bondStatus?.has_bond ? (
          /* No Bond - Show connect options */
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl border border-pink-500/20">
              <Heart className="w-12 h-12 mx-auto mb-3 text-pink-500" />
              <h3 className="font-semibold text-primary mb-2">Connect with Your Partner</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Link your accounts to share your wellness journey, see each other's vibes, and heal together.
              </p>
            </div>

            <div className="grid gap-3">
              <Button
                onClick={createInvite}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Create Invite Code
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowJoin(true)}
                className="w-full border-pink-500/30"
              >
                <Users className="w-4 h-4 mr-2" />
                I Have a Code
              </Button>
            </div>

            {/* Privacy Note */}
            <div className="p-3 bg-violet-500/10 rounded-lg border border-violet-500/20">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-violet-500 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-violet-600 dark:text-violet-400">Privacy First</p>
                  <p className="text-[10px] text-muted-foreground">
                    You choose what to share. Health data like symptoms and cycle tracking are private by default.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Has Bond - Show partner view */
          <div className="space-y-4 py-4">
            {/* Partner Card */}
            <Card className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                      <span className="text-2xl">💑🏾</span>
                    </div>
                    <div>
                      <p className="font-semibold text-primary">{bondStatus.partner_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {bondStatus.days_together} days healing together 💞
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPrivacy(true)}
                    className="text-muted-foreground"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>

                {/* Partner's Shared Data */}
                {bondStatus.partner_shared_data && (
                  <div className="space-y-3 pt-3 border-t border-pink-500/20">
                    {/* Partner's Vibe */}
                    {bondStatus.partner_shared_data.vibe_checks?.length > 0 && (
                      <div className="bg-background/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Their Recent Vibe</p>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {bondStatus.partner_shared_data.vibe_checks[0].mood === "great" ? "😄" :
                             bondStatus.partner_shared_data.vibe_checks[0].mood === "good" ? "🙂" :
                             bondStatus.partner_shared_data.vibe_checks[0].mood === "okay" ? "😐" :
                             bondStatus.partner_shared_data.vibe_checks[0].mood === "low" ? "😔" : "😢"}
                          </span>
                          <div>
                            <p className="text-sm font-medium capitalize">{bondStatus.partner_shared_data.vibe_checks[0].mood}</p>
                            <p className="text-[10px] text-muted-foreground">
                              Vibe Score: {bondStatus.partner_shared_data.vibe_checks[0].vibe_score}/10
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Partner's Zodiac */}
                    {bondStatus.partner_shared_data.zodiac && (
                      <div className="bg-background/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Their Cosmic Blueprint</p>
                        <div className="flex flex-wrap gap-2">
                          {bondStatus.partner_shared_data.zodiac.sun_sign && (
                            <Badge variant="outline" className="text-[10px]">
                              ☀️ {bondStatus.partner_shared_data.zodiac.sun_sign}
                            </Badge>
                          )}
                          {bondStatus.partner_shared_data.zodiac.moon_sign && (
                            <Badge variant="outline" className="text-[10px]">
                              🌙 {bondStatus.partner_shared_data.zodiac.moon_sign}
                            </Badge>
                          )}
                          {bondStatus.partner_shared_data.zodiac.life_path && (
                            <Badge variant="outline" className="text-[10px]">
                              🔢 Life Path {bondStatus.partner_shared_data.zodiac.life_path}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Partner's Recent Dreams */}
                    {bondStatus.partner_shared_data.dreams?.length > 0 && (
                      <div className="bg-background/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Their Recent Dreams</p>
                        <div className="space-y-1">
                          {bondStatus.partner_shared_data.dreams.slice(0, 2).map((dream, i) => (
                            <p key={i} className="text-xs text-primary">
                              🌙 {dream.title} {dream.is_lucid && "✨"}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="border-pink-500/30 hover:bg-pink-50"
                onClick={() => setShowSendLove(true)}
                data-testid="send-love-btn"
              >
                <Heart className="w-4 h-4 mr-1 text-pink-500" />
                Send Love
              </Button>
              <Button variant="outline" size="sm" className="border-violet-500/30">
                <Sparkles className="w-4 h-4 mr-1" />
                Manifest Together
              </Button>
            </div>

            {/* Disconnect Option */}
            <Button
              variant="ghost"
              size="sm"
              onClick={disconnectBond}
              className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Disconnect Soul Bond
            </Button>
          </div>
        )}

        {/* Invite Code Modal */}
        {showInvite && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm">
              <CardContent className="p-6 text-center">
                <Heart className="w-12 h-12 mx-auto mb-3 text-pink-500" />
                <h3 className="font-semibold text-lg mb-2">Your Invite Code</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Share this code with your partner
                </p>
                
                <div className="bg-muted rounded-lg p-4 mb-4 flex items-center justify-center gap-3">
                  <span className="text-2xl font-mono font-bold tracking-wider">{inviteCode}</span>
                  <Button variant="ghost" size="sm" onClick={copyCode}>
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground mb-4">
                  Code expires in 7 days
                </p>
                
                <Button onClick={() => setShowInvite(false)} className="w-full">
                  Done
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Join Modal */}
        {showJoin && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-2 text-center">Enter Invite Code</h3>
                <p className="text-sm text-muted-foreground mb-4 text-center">
                  Enter the code your partner shared with you
                </p>
                
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXXXX"
                  maxLength={8}
                  className="text-center text-xl font-mono tracking-wider mb-4"
                />
                
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowJoin(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={acceptInvite} className="flex-1 bg-pink-500 hover:bg-pink-600">
                    Connect 💞
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Privacy Settings Modal */}
        {showPrivacy && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm max-h-[80vh] overflow-y-auto">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-violet-500" />
                  Privacy Settings
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose what to share with your partner
                </p>
                
                <div className="space-y-3">
                  {bondStatus?.shareable_items?.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{item.emoji}</span>
                        <div>
                          <p className="text-sm font-medium">{item.name}</p>
                          <p className="text-[10px] text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={privacySettings[item.id] || false}
                        onCheckedChange={() => togglePrivacy(item.id)}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowPrivacy(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={savePrivacySettings} disabled={saving} className="flex-1">
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Send Love Modal */}
        {showSendLove && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm max-h-[80vh] overflow-y-auto">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mx-auto mb-3">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg">Send Love to {bondStatus?.partner_name}</h3>
                  <p className="text-sm text-muted-foreground">Choose a message or write your own</p>
                </div>
                
                {/* Preset Messages */}
                <div className="space-y-2 mb-4">
                  {LOVE_MESSAGES.map((msg, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendLoveMessage(msg.text)}
                      disabled={sendingLove}
                      className="w-full p-3 text-left bg-pink-50 dark:bg-pink-500/10 rounded-lg border border-pink-200 dark:border-pink-500/30 hover:bg-pink-100 dark:hover:bg-pink-500/20 transition-colors disabled:opacity-50"
                      data-testid={`preset-love-msg-${idx}`}
                    >
                      <span className="text-lg mr-2">{msg.emoji}</span>
                      <span className="text-sm text-primary">{msg.text}</span>
                    </button>
                  ))}
                </div>
                
                {/* Custom Message */}
                <div className="border-t border-border pt-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Or write your own:</p>
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Type your heartfelt message..."
                    maxLength={200}
                    className="min-h-[80px] text-sm"
                  />
                  <p className="text-[10px] text-muted-foreground text-right mt-1">{customMessage.length}/200</p>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => { setShowSendLove(false); setCustomMessage(""); }} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => sendLoveMessage(customMessage)}
                    disabled={sendingLove || !customMessage.trim()} 
                    className="flex-1 bg-pink-500 hover:bg-pink-600"
                    data-testid="send-custom-love-btn"
                  >
                    {sendingLove ? "Sending..." : "Send 💗"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HealingTogether;
